import { PermissionLevel } from '@/client/custom-roles';
import { PermissionPill } from '@/components/custom-roles';
import React from 'react';
import styled from 'styled-components';

export type RoleCardPill = {
  key: string;
  label: string;
  level: PermissionLevel;
};

type RoleCardProps = {
  name: string;
  description?: string;
  pills: RoleCardPill[];
  footer: string;
  onEdit?: () => void;
  onDelete?: () => void;
  editLabel?: string;
  deleteLabel?: string;
};

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--medium-padding); /* Figma 15px -> --medium-padding (16px) */
  flex: 1;
  padding: var(--default-padding-small);
  background-color: var(--white-main);
  border: 1px solid var(--gray-border-lightest);
  border-radius: var(--xsmall-border-radius);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--medium-padding);
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--xxsmall-padding); /* Figma 3px -> --xxsmall-padding (4px) */
`;

const Name = styled.h3`
  margin: 0;
  font-size: var(--medium-font-size);
  font-weight: var(--bold-font-weight);
  line-height: var(--default-line-height);
`;

const Description = styled.p`
  margin: 0;
  font-size: var(--xsmall-font-size);
  font-weight: var(--regular-font-weight);
  line-height: var(--default-line-height);
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: var(--xxsmall-padding);
  white-space: nowrap;
`;

const actionReset = `
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font-size: var(--xsmall-font-size);
  line-height: var(--default-line-height);
`;

const EditAction = styled.button`
  ${actionReset}
  color: var(--gray-medium-dark);
`;

const DeleteAction = styled.button`
  ${actionReset}
  color: var(--red-main);
`;

const Separator = styled.span`
  width: 1px;
  height: var(--xsmall-font-size);
  background-color: var(--gray-medium-dark);
`;

const Pills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--xsmall-padding); /* Figma 7px -> --xsmall-padding (8px) */
`;

const Footer = styled.span`
  font-size: var(--xsmall-font-size);
  font-weight: var(--regular-font-weight);
  line-height: var(--default-line-height);
`;

export const RoleCard: React.FC<RoleCardProps> = ({
  name,
  description,
  pills,
  footer,
  onEdit,
  onDelete,
  editLabel,
  deleteLabel,
}) => (
  <Card>
    <Header>
      <TitleBlock>
        <Name>{name}</Name>
        {description && <Description>{description}</Description>}
      </TitleBlock>

      {(onEdit || onDelete) && (
        <Actions>
          {onEdit && (
            <EditAction type="button" onClick={onEdit}>
              {editLabel}
            </EditAction>
          )}
          {onEdit && onDelete && <Separator aria-hidden="true" />}
          {onDelete && (
            <DeleteAction type="button" onClick={onDelete}>
              {deleteLabel}
            </DeleteAction>
          )}
        </Actions>
      )}
    </Header>

    <Pills>
      {pills.map((pill) => (
        <PermissionPill key={pill.key} label={pill.label} level={pill.level} />
      ))}
    </Pills>

    <Footer>{footer}</Footer>
  </Card>
);
